/**
 * In-app browser (embedded webview) detection · 2026-07-13.
 *
 * Google hard-blocks OAuth inside embedded webviews with
 * "Error 403: disallowed_useragent" (their "Use secure browsers" policy).
 * Wedge is Google-OAuth-first, so a rep who opens their sign-in link from
 * inside Instagram / Facebook / LinkedIn / TikTok / the Gmail app lands on
 * a dead-end Google error page with no way forward.
 *
 * This module detects those webviews BEFORE we redirect to Google so the
 * landing page can steer the rep to a real browser (or the magic-link
 * path, which never touches Google and works fine inside a webview).
 *
 * Deliberately conservative:
 *  · The installed home-screen PWA (standalone display mode) is NEVER
 *    flagged · that is the money path and it works today.
 *  · Real browsers that embed "Safari/" in their UA (Safari, CriOS,
 *    FxiOS, EdgiOS) are never flagged.
 *  · Detection is a pure function of (ua, standalone) so it is testable.
 */

export type InAppPlatform = 'ios' | 'android' | 'other';

export interface InAppBrowserInfo {
  inApp: boolean;
  /** Human label for the host app when a known token matched · null when
   *  only the generic webview heuristic fired. */
  app: string | null;
  platform: InAppPlatform;
}

/* Known host-app UA tokens, checked in order. Each entry: [regex, label]. */
const KNOWN_APPS: Array<[RegExp, string]> = [
  [/Instagram/i, 'Instagram'],
  [/FBAN|FBAV|FB_IAB|FBIOS|MessengerForiOS|MessengerLite/i, 'Facebook'],
  [/LinkedInApp/i, 'LinkedIn'],
  [/musical_ly|TikTok|Bytedance/i, 'TikTok'],
  [/Snapchat/i, 'Snapchat'],
  [/Pinterest/i, 'Pinterest'],
  [/MicroMessenger/i, 'WeChat'],
  [/\bLine\//i, 'LINE'],
  [/\bGSA\//i, 'the Google app'],
  [/Twitter/i, 'X'],
];

function platformOf(ua: string): InAppPlatform {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * Pure detection · pass ua + standalone explicitly (tests) or omit to read
 * from the live environment.
 */
export function detectInAppBrowser(ua?: string, standalone?: boolean): InAppBrowserInfo {
  /* 2026-07-27 · the Capacitor shell IS the native app, not somebody else's
     embedded browser — but its bare-WKWebView UA (no "Safari/" token) matched
     the iOS heuristic below, so every guard surface (CodeRedeem, AccessDenied,
     RequestAccessForm, WedgeGrant) told App Store users "you're inside an
     app's built-in browser · Google blocks sign-in here" and offered escape
     hatches (x-safari-https:// jump, magic-link email) that are dead or
     actively strand them outside the app. Short-circuit first. */
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) {
      return { inApp: false, app: null, platform: 'ios' };
    }
  } catch { /* SSR / non-browser · fall through to the heuristics */ }
  let agent = ua;
  let isStandalone = standalone;
  try {
    if (agent === undefined) agent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (isStandalone === undefined) {
      isStandalone =
        (typeof navigator !== 'undefined' &&
          (navigator as unknown as { standalone?: boolean }).standalone === true) ||
        (typeof window !== 'undefined' &&
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(display-mode: standalone)').matches);
    }
  } catch {
    /* SSR / locked-down env · treat as a normal browser */
  }
  const uaStr = agent || '';
  const platform = platformOf(uaStr);
  const notInApp: InAppBrowserInfo = { inApp: false, app: null, platform };

  /* Our own installed PWA · never flag, this is the working money path. */
  if (isStandalone) return notInApp;
  if (!uaStr) return notInApp;

  for (const [re, label] of KNOWN_APPS) {
    if (re.test(uaStr)) return { inApp: true, app: label, platform };
  }

  /* Android webview · Chrome-based UAs carry "; wv" (and often
     "Version/X.Y" alongside Chrome, which real Chrome never has). */
  if (platform === 'android') {
    if (/;\s*wv[);]/.test(uaStr)) return { inApp: true, app: null, platform };
    if (/Version\/\d[\d.]*\s+Chrome\//.test(uaStr)) return { inApp: true, app: null, platform };
  }

  /* iOS WKWebView · every policy-compliant iOS browser (Safari, CriOS,
     FxiOS, EdgiOS, SFSafariViewController) ends its UA with "Safari/…".
     Bare WKWebViews do not. Standalone PWA was excluded above. */
  if (platform === 'ios' && !/Safari\//.test(uaStr)) {
    return { inApp: true, app: null, platform };
  }

  return notInApp;
}

/**
 * Best-effort jump from the webview to the system browser.
 * Returns true when an escape was attempted (not a guarantee it worked ·
 * some webviews silently ignore these schemes).
 */
export function tryOpenInSystemBrowser(info: InAppBrowserInfo, href?: string): boolean {
  try {
    const target = href || (typeof window !== 'undefined' ? window.location.href : '');
    if (!target) return false;
    if (info.platform === 'android') {
      const u = new URL(target);
      const fallback = encodeURIComponent(target);
      window.location.href =
        `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;` +
        `S.browser_fallback_url=${fallback};end`;
      return true;
    }
    if (info.platform === 'ios') {
      /* Honored by several webviews (Instagram among them) · silently
         ignored elsewhere, so callers still show manual instructions. */
      window.location.href = `x-safari-https://${target.replace(/^https?:\/\//, '')}`;
      return true;
    }
  } catch {
    /* URL parse or navigation blocked · fall through */
  }
  return false;
}
